import {
	CheckOutlined,
	DeleteOutlined,
	LeftOutlined,
	LoadingOutlined,
	PlusOutlined,
} from "@ant-design/icons";
import { getClientEnv } from "@shared/env";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Image, Input, message, Select, Upload } from "antd";
import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { filesApi } from "@/modules/objects/api/files.api";
import { UzPhoneInput } from "@/shared/components/UzPhoneInput";
import type { AppRole } from "@/shared/config/navigation";
import { canCreateClient } from "@/shared/config/permissions";
import { parseApiError } from "@/shared/utils/error";
import { normalizeUzPhoneInput } from "@/shared/utils/uz-phone";
import { getClientById, submitClientSave } from "../services/clients.backend";
import type { ClientDetail } from "../types/clients.types";

type LeadClientState = {
	lead?: {
		name: string;
		phone: string;
	};
};

type ClientFormState = {
	firstName: string;
	lastName: string;
	patronymic: string;
	birthDate: string;
	gender: "erkak" | "ayol";
	phone: string;
	phoneAdd1: string;
	phoneAdd2: string;
	region: string;
	district: string;
	address: string;
	passportSeries: string;
	jshshir: string;
	issueDate: string;
	issuedBy: string;
	passportFrontImage: string;
	passportBackImage: string;
};

export function normalizeClientPhoneInput(phone?: string) {
	return normalizeUzPhoneInput(phone);
}

export function getClientFormImageUrl(apiUrl: string, image?: string) {
	if (!image) {
		return undefined;
	}
	if (image.startsWith("http://") || image.startsWith("https://")) {
		return image;
	}
	if (image.startsWith("/uploads/")) {
		return `${apiUrl}${image}`;
	}
	return `${apiUrl}/uploads/${image}`;
}

export function getClientFormState(client: ClientDetail): ClientFormState {
	return {
		firstName: client.firstName || "",
		lastName: client.lastName || "",
		patronymic: client.patronymic || "",
		birthDate: client.birthDate ? client.birthDate.split("T")[0] : "",
		gender: client.gender || "erkak",
		phone: normalizeClientPhoneInput(client.phone),
		phoneAdd1: normalizeClientPhoneInput(client.phoneAdd1),
		phoneAdd2: normalizeClientPhoneInput(client.phoneAdd2),
		region: client.region || "Toshkent",
		district: client.district || "",
		address: client.address || "",
		passportSeries: client.passport?.seriesNumber || "",
		jshshir: client.passport?.jshshir || "",
		issueDate: client.passport?.issueDate ? client.passport.issueDate.split("T")[0] : "",
		issuedBy: client.passport?.issuedBy || "",
		passportFrontImage: client.passport?.passportFrontImage || "",
		passportBackImage: client.passport?.passportBackImage || "",
	};
}

export function ClientFormPage() {
	const navigate = useNavigate();
	const location = useLocation();
	const { role } = useOutletContext<{ role: AppRole }>();
	const queryClient = useQueryClient();
	const { clientId } = useParams();
	const isEditMode = !!clientId;
	const leadState = (location.state as LeadClientState | null)?.lead;
	const env = getClientEnv();

	// Form states
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [patronymic, setPatronymic] = useState("");
	const [birthDate, setBirthDate] = useState("");
	const [gender, setGender] = useState<"erkak" | "ayol">("erkak");
	const [phone, setPhone] = useState("");
	const [phoneAdd1, setPhoneAdd1] = useState("");
	const [phoneAdd2, setPhoneAdd2] = useState("");

	const [region, setRegion] = useState("Toshkent");
	const [district, setDistrict] = useState("");
	const [address, setAddress] = useState("");
	const [passportSeries, setPassportSeries] = useState("");
	const [jshshir, setJshshir] = useState("");
	const [issueDate, setIssueDate] = useState("");
	const [issuedBy, setIssuedBy] = useState("");
	const [passportFrontImage, setPassportFrontImage] = useState("");
	const [passportBackImage, setPassportBackImage] = useState("");
	const [uploadingFrontImage, setUploadingFrontImage] = useState(false);
	const [uploadingBackImage, setUploadingBackImage] = useState(false);

	const { data: client, isLoading } = useQuery({
		queryKey: ["client", clientId],
		queryFn: () => getClientById(clientId!),
		enabled: isEditMode,
	});

	// Load existing client details if editing
	useEffect(() => {
		if (isEditMode) {
			if (client) {
				const formState = getClientFormState(client);
				setFirstName(formState.firstName);
				setLastName(formState.lastName);
				setPatronymic(formState.patronymic);
				setBirthDate(formState.birthDate);
				setGender(formState.gender);
				setPhone(formState.phone);
				setPhoneAdd1(formState.phoneAdd1);
				setPhoneAdd2(formState.phoneAdd2);
				setRegion(formState.region);
				setDistrict(formState.district);
				setAddress(formState.address);
				setPassportSeries(formState.passportSeries);
				setJshshir(formState.jshshir);
				setIssueDate(formState.issueDate);
				setIssuedBy(formState.issuedBy);
				setPassportFrontImage(formState.passportFrontImage);
				setPassportBackImage(formState.passportBackImage);
			}
		} else if (leadState) {
			const [firstPart = "", ...restParts] = leadState.name.split(" ");
			setFirstName(firstPart);
			setLastName(restParts.join(" "));
			setPhone(normalizeClientPhoneInput(leadState.phone));
			setPassportFrontImage("");
			setPassportBackImage("");
		}
	}, [isEditMode, client, leadState]);

	// If we came from leads, cancel goes back to leads; otherwise back to clients
	const cancelPath = leadState ? "/leads" : isEditMode ? `/clients/${clientId}` : "/clients";

	const saveMutation = useMutation({
		mutationFn: submitClientSave,
		onSuccess: async (savedClient) => {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ["client", savedClient.id] }),
				queryClient.invalidateQueries({ queryKey: ["clients"] }),
				queryClient.invalidateQueries({ queryKey: ["sales"] }),
				queryClient.invalidateQueries({ queryKey: ["payment-schedules"] }),
				queryClient.invalidateQueries({ queryKey: ["payments"] }),
			]);
			if (isEditMode) {
				message.success("Mijoz ma'lumotlari muvaffaqiyatli saqlandi!");
			} else {
				message.success("Yangi mijoz muvaffaqiyatli qo'shildi!");
			}
			navigate(`/clients/${savedClient.id}`);
		},
		onError: (error) => {
			message.error(parseApiError(error));
		},
	});

	const handleSubmit = () => {
		if (!(firstName.trim() && lastName.trim() && phone.trim())) {
			message.error("Iltimos, majburiy maydonlarni to'ldiring (Ism, Familiya, Telefon)!");
			return;
		}

		saveMutation.mutate({
			id: clientId,
			firstName,
			lastName,
			patronymic,
			birthDate,
			gender,
			phone,
			phoneAdd1,
			phoneAdd2,
			region,
			district,
			address,
			passport: {
				seriesNumber: passportSeries,
				jshshir,
				issueDate,
				issuedBy,
				passportFrontImage,
				passportBackImage,
			},
		});
	};

	const handlePassportUpload = async (file: File, side: "front" | "back") => {
		const setUploading = side === "front" ? setUploadingFrontImage : setUploadingBackImage;
		const setImage = side === "front" ? setPassportFrontImage : setPassportBackImage;

		setUploading(true);

		try {
			const uploadedFile = await filesApi.uploadFile(file, "image");
			if (!uploadedFile.url) {
				message.error("Rasmni yuklashda xatolik yuz berdi");
				return Upload.LIST_IGNORE;
			}

			setImage(uploadedFile.url);
			message.success("Pasport rasmi yuklandi");
		} catch (error) {
			message.error(parseApiError(error));
		} finally {
			setUploading(false);
		}

		return false;
	};

	const clearPassportImage = (side: "front" | "back") => {
		if (side === "front") {
			setPassportFrontImage("");
			return;
		}

		setPassportBackImage("");
	};

	const regionOptions = [
		{ label: "Toshkent", value: "Toshkent" },
		{ label: "Farg'ona", value: "Farg'ona" },
		{ label: "Samarqand", value: "Samarqand" },
		{ label: "Andijon", value: "Andijon" },
		{ label: "Buxoro", value: "Buxoro" },
		{ label: "Namangan", value: "Namangan" },
		{ label: "Xorazm", value: "Xorazm" },
	];

	const fromDashboard = (location.state as any)?.fromDashboard === true;

	const handleBack = () => {
		if (fromDashboard) {
			navigate(-1);
			return;
		}

		navigate(cancelPath);
	};

	const passportFrontImageUrl = getClientFormImageUrl(env.VITE_API_URL, passportFrontImage);
	const passportBackImageUrl = getClientFormImageUrl(env.VITE_API_URL, passportBackImage);

	if (!(isEditMode || canCreateClient(role))) {
		return <Navigate replace to="/clients" />;
	}

	return (
		<div className="crm-client-form">
			<div className="crm-page-head">
				<div>
					<Button
						className="crm-back-link"
						icon={<LeftOutlined />}
						onClick={handleBack}
						type="text"
					>
						Bekor qilish
					</Button>
					<h1 style={{ marginTop: "4px" }}>
						{isEditMode ? "Mijozni tahrirlash" : "Yangi mijoz qo'shish"}
					</h1>
					<p>
						{fromDashboard ? (
							<span style={{ color: "var(--crm-text-secondary)" }}>Bosh sahifa / </span>
						) : null}
						Tashkilot mijozining shaxsiy va aloqa ma'lumotlari
					</p>
				</div>
				<div className="crm-head-actions">
					<Button onClick={handleBack}>Bekor qilish</Button>
					<Button
						className="crm-primary-btn"
						icon={<CheckOutlined />}
						onClick={handleSubmit}
						loading={saveMutation.isPending}
					>
						Saqlash
					</Button>
				</div>
			</div>

			{isLoading ? (
				<div style={{ padding: 60, textAlign: "center" }}>Yullanmoqda...</div>
			) : (
				<div className="crm-grid crm-form-grid crm-client-form-grid">
					{/* Personal Info Card */}
					<div className="crm-card">
						<div className="crm-card-head">
							<h3>Shaxsiy ma'lumotlar</h3>
						</div>
						<div
							className="crm-card-body"
							style={{ display: "flex", flexDirection: "column", gap: "12px" }}
						>
							<div className="crm-inline-fields">
								<div className="crm-field">
									<span>Ism *</span>
									<Input
										placeholder="Ismni kiriting"
										value={firstName}
										onChange={(e) => setFirstName(e.target.value)}
									/>
								</div>
								<div className="crm-field">
									<span>Familiya *</span>
									<Input
										placeholder="Familiyani kiriting"
										value={lastName}
										onChange={(e) => setLastName(e.target.value)}
									/>
								</div>
							</div>

							<div className="crm-field">
								<span>Sharif (otasining ismi)</span>
								<Input
									placeholder="Sharifini kiriting"
									value={patronymic}
									onChange={(e) => setPatronymic(e.target.value)}
								/>
							</div>

							<div className="crm-inline-fields">
								<div className="crm-field">
									<span>Tug'ilgan sana</span>
									<Input
										type="date"
										value={birthDate}
										onChange={(e) => setBirthDate(e.target.value)}
									/>
								</div>
								<div className="crm-field">
									<span>Jinsi</span>
									<Select
										value={gender}
										onChange={setGender}
										style={{ width: "100%" }}
										options={[
											{ label: "Erkak", value: "erkak" },
											{ label: "Ayol", value: "ayol" },
										]}
									/>
								</div>
							</div>

							<div className="crm-divider" style={{ margin: "8px 0" }} />
							<div
								className="crm-section-note"
								style={{ fontWeight: "bold", fontSize: "13px", marginBottom: "4px" }}
							>
								Aloqa ma'lumotlari
							</div>

							<div className="crm-field">
								<span>Asosiy telefon raqami *</span>
								<UzPhoneInput value={phone} onChange={setPhone} />
							</div>

							<div className="crm-inline-fields">
								<div className="crm-field">
									<span>Qo'shimcha telefon 1</span>
									<UzPhoneInput value={phoneAdd1} onChange={setPhoneAdd1} />
								</div>
								<div className="crm-field">
									<span>Qo'shimcha telefon 2</span>
									<UzPhoneInput value={phoneAdd2} onChange={setPhoneAdd2} />
								</div>
							</div>
						</div>
					</div>

					{/* Address & Passport Card */}
					<div className="crm-card">
						<div className="crm-card-head">
							<h3>Manzil va Pasport</h3>
						</div>
						<div
							className="crm-card-body"
							style={{ display: "flex", flexDirection: "column", gap: "12px" }}
						>
							<div className="crm-inline-fields">
								<div className="crm-field">
									<span>Viloyat</span>
									<Select
										value={region}
										onChange={setRegion}
										style={{ width: "100%" }}
										options={regionOptions}
									/>
								</div>
								<div className="crm-field">
									<span>Tuman</span>
									<Input
										placeholder="Mirobod tumani"
										value={district}
										onChange={(e) => setDistrict(e.target.value)}
									/>
								</div>
							</div>

							<div className="crm-field">
								<span>Manzil (Ko'cha, uy, xonadon)</span>
								<Input.TextArea
									rows={2}
									placeholder="Masalan: Nukus ko'chasi, 23-uy, 45-xonadon"
									value={address}
									onChange={(e) => setAddress(e.target.value)}
								/>
							</div>

							<div className="crm-divider" style={{ margin: "8px 0" }} />
							<div
								className="crm-section-note"
								style={{ fontWeight: "bold", fontSize: "13px", marginBottom: "4px" }}
							>
								Pasport ma'lumotlari
							</div>

							<div className="crm-inline-fields">
								<div className="crm-field">
									<span>Pasport seriyasi va raqami</span>
									<Input
										placeholder="AB1234567"
										value={passportSeries}
										onChange={(e) => setPassportSeries(e.target.value)}
									/>
								</div>
								<div className="crm-field">
									<span>JShShIR (14 xonali kod)</span>
									<Input
										placeholder="12345678901234"
										maxLength={14}
										value={jshshir}
										onChange={(e) => setJshshir(e.target.value)}
									/>
								</div>
							</div>

							<div className="crm-inline-fields">
								<div className="crm-field">
									<span>Berilgan sana</span>
									<Input
										type="date"
										value={issueDate}
										onChange={(e) => setIssueDate(e.target.value)}
									/>
								</div>
								<div className="crm-field">
									<span>Kim tomonidan berilgan</span>
									<Input
										placeholder="Toshkent sh. IIBB"
										value={issuedBy}
										onChange={(e) => setIssuedBy(e.target.value)}
									/>
								</div>
							</div>

							<div>
								<span
									className="crm-user-role"
									style={{ fontSize: "11px", display: "block", marginBottom: "6px" }}
								>
									Pasport rasmlari
								</span>
								<div style={{ display: "flex", gap: "12px" }}>
									<div className="crm-client-passport-cell" style={{ flex: "0 0 96px" }}>
										{uploadingFrontImage ? (
											<div className="crm-square-upload-btn">
												<LoadingOutlined />
											</div>
										) : passportFrontImageUrl ? (
											<div className="crm-square-upload-preview">
												<Image
													src={passportFrontImageUrl}
													alt="Old tomon"
													style={{
														width: "96px",
														height: "96px",
														objectFit: "cover",
														borderRadius: "12px",
													}}
													wrapperStyle={{ width: "96px", height: "96px" }}
												/>
												<Button
													type="text"
													icon={<DeleteOutlined style={{ color: "var(--crm-red)" }} />}
													onClick={(event) => {
														event.stopPropagation();
														clearPassportImage("front");
													}}
													className="crm-square-upload-delete"
												/>
											</div>
										) : (
											<Upload
												accept="image/*"
												beforeUpload={(file) => {
													handlePassportUpload(file, "front");
													return false;
												}}
												showUploadList={false}
											>
												<div className="crm-square-upload-btn">
													<PlusOutlined style={{ fontSize: "18px", color: "var(--crm-ink-3)" }} />
													<span
														style={{
															fontSize: "11px",
															fontWeight: 500,
															color: "var(--crm-ink-2)",
															marginTop: "4px",
														}}
													>
														Old tomon
													</span>
												</div>
											</Upload>
										)}
									</div>
									<div className="crm-client-passport-cell" style={{ flex: "0 0 96px" }}>
										{uploadingBackImage ? (
											<div className="crm-square-upload-btn">
												<LoadingOutlined />
											</div>
										) : passportBackImageUrl ? (
											<div className="crm-square-upload-preview">
												<Image
													src={passportBackImageUrl}
													alt="Orqa tomon"
													style={{
														width: "96px",
														height: "96px",
														objectFit: "cover",
														borderRadius: "12px",
													}}
													wrapperStyle={{ width: "96px", height: "96px" }}
												/>
												<Button
													type="text"
													icon={<DeleteOutlined style={{ color: "var(--crm-red)" }} />}
													onClick={(event) => {
														event.stopPropagation();
														clearPassportImage("back");
													}}
													className="crm-square-upload-delete"
												/>
											</div>
										) : (
											<Upload
												accept="image/*"
												beforeUpload={(file) => {
													handlePassportUpload(file, "back");
													return false;
												}}
												showUploadList={false}
											>
												<div className="crm-square-upload-btn">
													<PlusOutlined style={{ fontSize: "18px", color: "var(--crm-ink-3)" }} />
													<span
														style={{
															fontSize: "11px",
															fontWeight: 500,
															color: "var(--crm-ink-2)",
															marginTop: "4px",
														}}
													>
														Orqa tomon
													</span>
												</div>
											</Upload>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
export default ClientFormPage;
